
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useJobs } from '../contexts/JobsContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JobCard from '../components/JobCard';
import SEOHead from '../components/SEOHead';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BriefcaseBusiness, Search } from 'lucide-react';

// Function to calculate Levenshtein distance (edit distance) between two strings
const levenshteinDistance = (a: string, b: string): number => {
  const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= b.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + substitutionCost // substitution
      );
    }
  }

  return matrix[b.length][a.length];
};

// Function to check if a string is similar to another string
const isSimilar = (str1: string, str2: string, threshold = 0.7): boolean => {
  if (!str1 || !str2) return false;
  
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  // Exact match or substring match
  if (s2.includes(s1) || s1.includes(s2)) return true;
  
  // Check if one is a significant substring of the other
  if (s1.length > 3 && s2.includes(s1.substring(0, Math.floor(s1.length * 0.7)))) return true;
  if (s2.length > 3 && s1.includes(s2.substring(0, Math.floor(s2.length * 0.7)))) return true;
  
  // Calculate similarity using Levenshtein distance
  const maxLength = Math.max(s1.length, s2.length);
  if (maxLength === 0) return true;
  
  const distance = levenshteinDistance(s1, s2);
  const similarity = 1 - distance / maxLength;
  
  return similarity >= threshold;
};

const Jobs = () => {
  const { areas, jobs, getJobsByArea } = useJobs();
  const [selectedAreaId, setSelectedAreaId] = useState<string>('all');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<typeof jobs>([]);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Filter jobs based on selected area
  const areaFilteredJobs = selectedAreaId === 'all' 
    ? jobs.filter(job => job.status === 'active')
    : getJobsByArea(selectedAreaId);

  // Perform fuzzy search when search term changes
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (!searchTerm.trim()) {
        setSearchResults(areaFilteredJobs);
        return;
      }

      const searchTerms = searchTerm.toLowerCase().split(' ').filter(term => term.length > 0);
      
      const results = areaFilteredJobs.filter(job => {
        // Check each word in the search terms against job properties
        return searchTerms.some(term => {
          return (
            isSimilar(term, job.title.toLowerCase()) ||
            isSimilar(term, job.description.toLowerCase()) ||
            isSimilar(term, job.department.toLowerCase()) ||
            isSimilar(term, job.location.toLowerCase())
          );
        });
      });

      setSearchResults(results);
    }, 300); // Debounce search for better performance

    setSearchTimeout(timeout);
    
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchTerm, areaFilteredJobs]);

  // Initialize search results
  useEffect(() => {
    setSearchResults(areaFilteredJobs);
  }, [areaFilteredJobs]);

  const handleSelectJob = (id: string) => {
    setSelectedJobId(id === selectedJobId ? null : id);
  };

  // Generate structured data for job listings
  const generateJobListingsStructuredData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": searchResults.map((job, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "JobPosting",
          "title": job.title,
          "description": job.description,
          "datePosted": job.posted || new Date().toISOString(),
          "employmentType": job.type || "FULL_TIME",
          "hiringOrganization": {
            "@type": "Organization",
            "name": "TrueApply",
            "sameAs": window.location.origin
          },
          "jobLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": job.location,
              "addressCountry": "IN"
            }
          }
        }
      }))
    };
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead 
        title="TrueApply - Find and Apply to Jobs | Top Job Listings"
        description="Discover and apply to the latest job openings. TrueApply connects you with top employers and makes the application process simple and efficient."
        keywords="jobs, career, employment, job search, job listings, job application, hiring, work, job openings"
        ogTitle="Find Your Next Career Opportunity - TrueApply Job Portal"
        ogDescription="Browse hundreds of job listings from top companies. Apply online easily and track your applications with TrueApply's user-friendly job portal."
        structuredData={generateJobListingsStructuredData()}
      />
      
      <Header />
      
      <main className="flex-grow pt-28 pb-16 bg-apple-gray">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-apple-text mb-4">
              Explore Our Job Openings
            </h1>
            <p className="text-xl text-apple-text-secondary max-w-3xl mx-auto">
              Find your perfect role in our growing team
            </p>
          </div>

          {/* Search and filter section */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="text-apple-text-secondary" size={18} />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search jobs by title, description, or location..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-apple-blue focus:border-transparent transition-all"
                />
              </div>
              
              <div className="w-full md:w-64">
                <Select 
                  onValueChange={(value) => setSelectedAreaId(value)}
                  defaultValue="all"
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Areas</SelectItem>
                    {areas.map((area) => (
                      <SelectItem key={area.id} value={area.id}>
                        {area.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Jobs list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {searchResults.length === 0 ? (
              <div className="col-span-2 bg-white rounded-xl shadow-sm p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BriefcaseBusiness className="text-apple-text-secondary" size={24} />
                </div>
                <h3 className="text-lg font-medium text-apple-text mb-2">No jobs found</h3>
                <p className="text-apple-text-secondary mb-4">
                  Try adjusting your search criteria or check back later for new opportunities
                </p>
                <Button onClick={() => {
                  setSearchTerm('');
                  setSelectedAreaId('all');
                }}>
                  Reset Filters
                </Button>
              </div>
            ) : (
              searchResults.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  isSelected={job.id === selectedJobId}
                  onSelect={handleSelectJob}
                />
              ))
            )}
          </div>

          {/* Apply button section */}
          {selectedJobId && (
            <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 z-10 border-t border-gray-200">
              <div className="container mx-auto px-6 flex justify-between items-center">
                <div className="text-apple-text">
                  <span className="font-medium">Selected: </span>
                  {jobs.find(job => job.id === selectedJobId)?.title}
                </div>
                <Link to={`/signup?jobId=${selectedJobId}`}>
                  <Button size="lg">
                    Apply for this Position
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Jobs;
